import { useState, useEffect } from "react";
import { Stack, DetailsList, DetailsListLayoutMode, IColumn, SelectionMode, Dialog, DialogType, DialogFooter, Spinner, SpinnerSize, DefaultButton, PrimaryButton } from "@fluentui/react";
import { getNGWords, deleteNGWord } from "../../../api/admin/api_admin";
import { NGWord, NGWordsResponse } from "../../../api/admin/models_admin";
import { AdminLabel, LinkButton, CellContent, formatDateTime, ErrorDialog } from "../components";

export const NGWordList = () => {
    const [ngwordsData, setNGWordsData] = useState<NGWordsResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState<boolean>(false);
    const [ngwordToDelete, setNGWordToDelete] = useState<NGWord | null>(null);
    const [deleting, setDeleting] = useState<boolean>(false);

    useEffect(() => {
        fetchNGWords();
    }, []);

    const fetchNGWords = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getNGWords();
            if ("error" in data) {
                setError(data.error);
            } else {
                setNGWordsData(data);
            }
        } catch (err) {
            setError("禁止ワード情報の取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (ngword: NGWord) => {
        setNGWordToDelete(ngword);
        setDeleteDialogVisible(true);
    };

    const handleDeleteConfirm = async () => {
        if (!ngwordToDelete) return;
        
        setDeleting(true);
        try {
            const result = await deleteNGWord(ngwordToDelete.NGW_seq);
            if (result.success) {
                setDeleteDialogVisible(false);
                setNGWordToDelete(null);
                await fetchNGWords();
            } else {
                setError(result.message || "削除に失敗しました");
                setDeleteDialogVisible(false);
            }
        } catch (err) {
            setError("削除中にエラーが発生しました");
            setDeleteDialogVisible(false);
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogVisible(false);
        setNGWordToDelete(null);
    };

    const canModify = (): boolean => {
        if (!ngwordsData) return false;
        const modifyFunc = ngwordsData.functions['modify_ngword'];
        return modifyFunc && 
               ngwordsData.current_user.authority_level >= modifyFunc.need_authority_level;
    };

    const canDelete = (): boolean => {
        if (!ngwordsData) return false;
        const deleteFunc = ngwordsData.functions['delete_ngword'];
        return deleteFunc && 
               ngwordsData.current_user.authority_level >= deleteFunc.need_authority_level;
    };

    const canAddNGWord = (): boolean => {
        if (!ngwordsData) return false;
        const addFunc = ngwordsData.functions['add_ngword'];
        return addFunc && ngwordsData.current_user.authority_level >= addFunc.need_authority_level;
    };

    const getNGLevelText = (level: number): string => {
        if (level === 1) return "1 : 警告表示";
        if (level === 2) return "2 : 生成不可";
        return level.toString();
    };
    const actionWidth = 30 + (canModify() ? 60 : 0) + (canDelete() ? 60 : 0)
    const columns: IColumn[] = [
        {
            key: 'actions',
            name: '',
            minWidth: actionWidth,
            maxWidth: actionWidth,
            onRender: (ngword: NGWord) => (
                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    {canModify() && (
                        <LinkButton 
                            link_to={`/menu/ngwordslist/modify?seq=${ngword.NGW_seq}`} 
                            text="変更" 
                            styles={{ root: { minWidth: 60 } }} 
                        />
                    )}
                    {canDelete() && (
                        <DefaultButton 
                            text="削除"
                            onClick={() => handleDeleteClick(ngword)}
                            styles={{ root: { minWidth: 60 } }}
                        />
                    )}
                </Stack>
            )
        },
        {
            key: 'ng_word',
            name: '禁止ワード',
            fieldName: 'NGW_ng_word',
            minWidth: 150,
            maxWidth: 250,
            isResizable: true,
            onRender: (ngword: NGWord) => (
                <CellContent>{ngword.NGW_ng_word}</CellContent>
            )
        },
        {
            key: 'ng_level',
            name: '禁止レベル',
            fieldName: 'NGW_ng_level',
            minWidth: 120,
            maxWidth: 150,
            isResizable: true,
            onRender: (ngword: NGWord) => (
                <CellContent>{getNGLevelText(ngword.NGW_ng_level)}</CellContent>
            )
        },
        {
            key: 'group',
            name: 'グループ',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
            onRender: (ngword: NGWord) => (
                <CellContent>
                    {ngword.NGW_group_cd === 0 ? "" : `${ngword.NGW_group_cd} : ${ngword.GRP_group_name || ""}`}
                </CellContent>
            )
        },
        {
            key: 'entry_datetime',
            name: '登録日時',
            fieldName: 'NGW_entry_datetime',
            minWidth: 150,
            maxWidth: 180,
            isResizable: true,
            onRender: (ngword: NGWord) => (
                <CellContent>{formatDateTime(ngword.NGW_entry_datetime)}</CellContent>
            )
        },
        {
            key: 'update_datetime',
            name: '更新日時',
            fieldName: 'NGW_update_datetime',
            minWidth: 150,
            maxWidth: 180,
            isResizable: true,
            onRender: (ngword: NGWord) => (
                <CellContent>{formatDateTime(ngword.NGW_update_datetime)}</CellContent>
            )
        }
    ];

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="禁止ワード一覧" />

            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }
            {ngwordsData && (
                <>
                    <DetailsList
                        items={ngwordsData.ngwords}
                        columns={columns}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionMode={SelectionMode.none}
                        isHeaderVisible={true}
                    />
                    
                    {canAddNGWord() && (
                        <Stack horizontal>
                            <LinkButton link_to="/menu/ngwordslist/add" text="新規登録" />
                        </Stack>
                    )}
                </>
            )}

            <Dialog
                hidden={!deleteDialogVisible}
                onDismiss={handleDeleteCancel}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: '削除確認',
                    subText: `禁止ワード「${ngwordToDelete?.NGW_ng_word}」を削除しますか?`
                }}
            >
                <DialogFooter>
                    <PrimaryButton 
                        onClick={handleDeleteConfirm} 
                        text="削除" 
                        disabled={deleting}
                    />
                    <DefaultButton 
                        onClick={handleDeleteCancel} 
                        text="キャンセル" 
                        disabled={deleting}
                    />
                </DialogFooter>
            </Dialog>
        </Stack>
    );
}